import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  OutageEvent,
  Scalars,
  AccountDetail,
  OutageLookupResponse,
  PremiseInfo,
  Maybe,
} from '../../../__generated__/pge-types';
import MapElement from '../../utility/map-element';
import {
  Data,
  Marker,
  MarkerClusterer,
  OverlayView,
} from '@react-google-maps/api';
import colors from '../../../themes/main-colors';
import serviceTerritory from './service_territory.json';
import useAuth from '../../../hooks/useAuth';
import useUtils, { AccountDetailsList } from '../outage-center/utils';
import { isLocationWithinPGEServiceBoundary } from '../../../util/map-utils';
import { useTranslation } from '../../../hooks/useTranslation';
import { IconButton } from '@material-ui/core';
import clsx from 'clsx';
import NearMeRoundedIcon from '@material-ui/icons/NearMeRounded';
import {
  Coordinates,
  NotificationRequestAccountParams,
} from '../../outage/OutageCenterPage';
import { useIsMobile } from '../../../util/style-utils';
import { featureFlagsContext } from '../../../providers/FeatureFlagsProvider';

export const DEFAULT_MAP_LAT = 45.5051;
export const DEFAULT_MAP_LNG = -122.675;
export const MIN_START_ZOOM = 12;
export const MIN_MAP_ZOOM = 8;
export const MAX_MAP_ZOOM = 15;
export const MAX_CLUSTER_ZOOM = 14;

//Bound restriction for the Service Territory
export const PGE_SERVICE_BOUND_RESTRICTION = {
  north: 45.883, //45.883463369942405, -121.52473667082151
  east: -121.524,
  south: 44.756, //44.756834406783746, -124.52523870226115
  west: -124.525,
};
const getPixelPositionOffset = (width: number, height: number) => ({
  x: -(width / 2),
  y: -(height / 2),
});

interface StyleProps {
  isMobileView: boolean;
  isNativeMobileView: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: 0,
      '& .mapCluster': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '53px !important',
        height: '53px !important',
      },
      '& div.mapCluster > div': {
        padding: '35%',
        width: '53px !important',
        height: '53px !important',
        position: 'absolute !important',
        top: '1.5px !important',
        left: '-7.45px !important',
        lineHeight: '0px !important',
        color: `${colors.white} !important`,
      },
      '& div.mapCluster[title="cluster-large"] > div': {
        top: '4.5px !important',
        left: '-6px !important',
      },
      '&  div': {
        fontSize: '0.69rem  !important',
        fontFamily: 'Arial,sans-serif',
        fontWeight: 'bold',
      },
      '& .gm-bundled-control .gmnoprint div': {
        borderRadius: '25px !important',
      },
      '& .gm-bundled-control .gmnoprint div > div': {
        display: 'none !important',
      },
    },
    rootContainer: (props: StyleProps) => ({
      '& .mapContainterRoot': {
        minHeight: `calc(100vh - ${
          props.isNativeMobileView ? '0' : props.isMobileView ? '88' : '64'
        }px)`,
      },
    }),
    legendButton: {
      position: 'absolute',
      outline: 0,
      background: '#FFFFFF 0% 0% no-repeat padding-box',
      top: 'auto',
      bottom: '120px',
      right: '10px',
      width: '40px',
      height: '40px',
      padding: 0,
      zIndex: 1,
      '&:hover': {
        backgroundColor: colors.white,
      },
      [theme.breakpoints.down('sm')]: {
        top: '130px',
        bottom: 'auto',
      },
      [theme.breakpoints.up('md')]: {
        top: 'auto !important',
      },
    },
    mobAppLegendButton: {
      [theme.breakpoints.down('sm')]: {
        top: '130px',
        bottom: 'auto',
      },
      backgroundColor: colors.white,
      '&:hover': {
        backgroundColor: colors.white,
      },
    },
    legendButtonIcon: {
      height: '30px',
      width: '30px',
      color: colors.noirBlur,
      position: 'absolute',
      top: '10px',
      left: '10px',
    },
  }),
);

export interface OutageCenterMapProps
  extends React.HTMLAttributes<HTMLDivElement> {
  mapData: OutageEvent[] | null | undefined;
  onMarkerClick?: (index: number, outage: OutageEvent) => void;
  mapType?: string;
  accountDetails?: AccountDetail[];
  isNativeMobileAppPage?: boolean;
  setSinglePremise?: (
    data: OutageLookupResponse,
    accountNumber?: string,
    accountIdentifier?: string,
  ) => void;
  setMultiPremise?: (data: OutageLookupResponse) => void;
  center?: Coordinates | null | undefined;
  outageBannerNotificationHeight: number;
  setLoggedInUserNotificationRequestAccountParams: React.Dispatch<
    React.SetStateAction<NotificationRequestAccountParams>
  >;
  showSelectedEvent?: boolean;
  notifFormSuccessfullySubmitted: boolean;
  setNotifFormSuccessfullySubmitted: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

type Position = { lat: Scalars['Latitude']; lng: Scalars['Longitude'] };

type FinalMarkerType = {
  position: Position;
  id: string;
  onClick?: (index: number, item: OutageEvent) => void;
  item?: OutageEvent;
};

type MarkerList = {
  isPrimaryAccount: boolean;
  hasKnownOutage: boolean;
  lat: Scalars['Latitude'];
  lng: Scalars['Longitude'];
  premiseInfo: Maybe<PremiseInfo>;
  accountNumber: string;
  description: string | undefined;
  encryptedPersonId: string;
};

const geoJsonFeature = {
  type: 'Feature',
  geometry: serviceTerritory,
};

const getImagePath = () => {
  const baseUrl = !(typeof window === 'undefined')
    ? window.location.origin
    : '';
  return `${baseUrl}/static/outageMapIcons/`;
};

const getResedentialIcon = (hasKnownOutage: boolean) => {
  const iconList = {
    primaryAccountWithOutage: `${getImagePath()}primaryAccountOutageReported.png`,
    primaryAccountWithoutOutage: `${getImagePath()}primaryAccountNoOutage.png`,
  };
  if (hasKnownOutage) {
    return iconList.primaryAccountWithOutage;
  } else {
    return iconList.primaryAccountWithoutOutage;
  }
};

const showCurrentLocation = (currentUserLocation: Position) => {
  return (
    <Marker
      icon={{
        url: `${getImagePath()}currentLocation.png`,
        scaledSize: new google.maps.Size(25, 25),
      }}
      position={{
        lat: currentUserLocation?.lat || DEFAULT_MAP_LAT,
        lng: currentUserLocation?.lng || DEFAULT_MAP_LNG,
      }}
    />
  );
};

const OutageCenterMapCore: FunctionComponent<OutageCenterMapProps> = ({
  mapData,
  onMarkerClick,
  children,
  mapType,
  accountDetails,
  isNativeMobileAppPage,
  setSinglePremise,
  setMultiPremise,
  center,
  id,
  outageBannerNotificationHeight,
  setLoggedInUserNotificationRequestAccountParams,
  showSelectedEvent,
  notifFormSuccessfullySubmitted,
  setNotifFormSuccessfullySubmitted,
}) => {
  const {
    buildLeftPanelDataFromAccountPremise,
    getOutageAccountDetails,
  } = useUtils();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { isPlannedDownTime } = useContext(featureFlagsContext);
  const [map, setMap] = useState<google.maps.Map | undefined>();
  const [
    selectedOutageEvent,
    setSelectedOutageEvent,
  ] = useState<OutageEvent | null>();
  const [hasMoved, setHasMoved] = useState(false);
  const [currentUserLocation, setCurrentUserLocation] = useState<Position>();
  const accountsDisplay =
    accountDetails && accountDetails.length
      ? getOutageAccountDetails(accountDetails)
      : null;

  const classes = useStyles({
    isMobileView: isMobile,
    isNativeMobileView: Boolean(isNativeMobileAppPage),
  });
  const renderAccountMarkers = (accountDetailsInfo: AccountDetailsList[]) => {
    const markerList: MarkerList[] = [];
    accountDetailsInfo.slice(0, 5).forEach(account => {
      //show only upto 5 accounts
      const {
        isPrimaryAccount,
        premiseInfos,
        accountNumber,
        description,
        encryptedPersonId,
      } = account;
      premiseInfos?.forEach(servicePoint => {
        if (servicePoint?.servicePointDetails) {
          const outageServicePoint = servicePoint?.servicePointDetails.find(
            outage => outage?.outageInfo?.hasKnownOutage,
          );
          const point =
            outageServicePoint || servicePoint?.servicePointDetails[0];
          markerList.push({
            isPrimaryAccount: isPrimaryAccount,
            lat: point?.lat,
            lng: point?.long,
            hasKnownOutage: point?.outageInfo?.hasKnownOutage || false,
            premiseInfo: servicePoint,
            accountNumber: accountNumber || '',
            description: String(description),
            encryptedPersonId: encryptedPersonId || '',
          });
        }
      });
    });

    //Setting default view based on the number of accounts
    if (markerList.length === 1) {
      const { lat, lng } = markerList[0];
      if (lat && lng) {
        map?.setZoom(MAX_MAP_ZOOM);
        map?.panTo({ lat, lng });
      }
    } else if (markerList.length >= 1) {
      const bounds = new google.maps.LatLngBounds();

      markerList.forEach(({ lat, lng }) => {
        if (!lat || !lng) {
          return false;
        }
        const position = new google.maps.LatLng(lat, lng);
        bounds.extend(position);
      });

      map?.fitBounds(bounds);
      //PanTo is not working properly with fitbounds to center the map with bounds
      setTimeout(() => {
        map?.panTo(bounds.getCenter());
      }, 50);
    }

    return markerList.map(marker => {
      const { lat, lng, hasKnownOutage } = marker;
      return (
        <OverlayView
          position={{
            lat,
            lng,
          }}
          mapPaneName={OverlayView.FLOAT_PANE}
          getPixelPositionOffset={getPixelPositionOffset}
        >
          <div>
            <img
              src={getResedentialIcon(hasKnownOutage)}
              title="Account Location"
              onClick={() => {
                if (marker?.premiseInfo) {
                  const {
                    premiseInfo: premise,
                    accountNumber,
                    description,
                    isPrimaryAccount,
                    encryptedPersonId,
                  } = marker;

                  if (premise?.servicePointDetails) {
                    const data = buildLeftPanelDataFromAccountPremise(premise);
                    const accountName = `${
                      description ? description : accountNumber
                    }${isPrimaryAccount ? ` (${t('PRIMARY')})` : ''}`;
                    setLoggedInUserNotificationRequestAccountParams({
                      accountNumber,
                      encryptedPersonId,
                    });
                    data &&
                      setSinglePremise &&
                      setSinglePremise(data, accountNumber, accountName);
                    setNotifFormSuccessfullySubmitted(
                      !notifFormSuccessfullySubmitted,
                    );

                    // To zoom & center the map when account icon is clicked
                    const nowZoom = map?.getZoom();
                    const position = new google.maps.LatLng(
                      lat || DEFAULT_MAP_LAT,
                      lng || DEFAULT_MAP_LNG,
                    );
                    map?.setZoom(
                      nowZoom && nowZoom > MIN_START_ZOOM ? nowZoom : 14,
                    );
                    map?.setCenter(position);
                  }
                }
              }}
            />
          </div>
        </OverlayView>
      );
    });
  };

  const handleMapMoved = () => {
    if (hasMoved) {
      return;
    }
    setHasMoved(true);
  };

  useEffect(() => {
    if (map && center) {
      setTimeout(() => {
        map?.setZoom(14);
        map?.panTo(center);
      }, 300);
    }
  }, [center]);

  useEffect(() => {
    if (map) {
      // set user current latitude, longitude
      navigator.geolocation?.getCurrentPosition(position => {
        const {
          latitude: userCurrentLatitude,
          longitude: userCurrentLongitude,
        } = position.coords;

        //Check if user location is within PGE service boundary
        if (
          isLocationWithinPGEServiceBoundary({
            lat: userCurrentLatitude,
            lng: userCurrentLongitude,
          }) &&
          (currentUserLocation?.lat !== userCurrentLatitude ||
            currentUserLocation?.lng !== userCurrentLongitude)
        ) {
          setCurrentUserLocation({
            lat: userCurrentLatitude,
            lng: userCurrentLongitude,
          });
        }
      });
    }
  }, [map]);

  const mapBounds = useRef<any>();
  const renderOutages = () => {
    if (!mapData) {
      return null;
    }
    let count = 0;
    const bounds = new google.maps.LatLngBounds();
    const getMarkers = () =>
      mapData.map((item: OutageEvent, index: number) => {
        if (!item || !item.lat || !item.long) {
          throw new Error('Latitude longitude');
        }
        count++;
        const latitude = Number(item.lat);
        const longitude = Number(item.long);
        const position = new google.maps.LatLng(
          latitude || DEFAULT_MAP_LAT,
          longitude || DEFAULT_MAP_LNG,
        );
        bounds.extend(position);

        return {
          position: { lat: latitude, lng: longitude },
          id: `marker-${index}`,
          onClick: (i: number, outageEvent: OutageEvent) => {
            if (!!onMarkerClick) {
              setSelectedOutageEvent(outageEvent);
              onMarkerClick(i, outageEvent);
            }
          },
          item,
        };
      });

    const finalMarkers: FinalMarkerType[] = getMarkers();

    if (!bounds.equals(mapBounds.current) && !hasMoved && count > 0) {
      mapBounds.current = bounds;
      map?.fitBounds(bounds);
      const nowZoom = map?.getZoom();
      if (!!nowZoom && nowZoom > MIN_START_ZOOM) {
        map?.setZoom(MIN_START_ZOOM);
      }
    }

    // to zoom & center the map if an event is selected although the map is dragged or zoomed out
    if (showSelectedEvent && selectedOutageEvent) {
      const nowZoom = map?.getZoom();
      const position = new google.maps.LatLng(
        Number(selectedOutageEvent.lat) || DEFAULT_MAP_LAT,
        Number(selectedOutageEvent.long) || DEFAULT_MAP_LNG,
      );
      map?.setZoom(nowZoom && nowZoom > MIN_START_ZOOM ? nowZoom : 14);
      map?.panTo(position);
    }

    const clustererOptions = {
      clusterClass: 'mapCluster',
      // calculating the value of group marker value by summing up individual marker
      // https://github.com/gmaps-marker-clusterer/gmaps-marker-clusterer/blob/c9d448d62b5634db4e76b6b7e3d913703cbed303/src/markerclusterer.js#L458
      // https://stackoverflow.com/questions/12783250/modifying-markerclusterer-icons-for-top-score-rather-than-marker-count
      calculator: (markers: any[], numStyles: number) => {
        let index = 0;
        let dv = markers.length;
        while (dv !== 0) {
          dv = parseInt(`${dv / 10}`, 10);
          index++;
        }

        const totalValue = markers.reduce((acc: number, marker) => {
          const value =
            marker.label.text === '1K+'
              ? 1000
              : marker.label.text === '99+'
              ? 100
              : parseInt(marker.label.text);
          return acc + value;
        }, 0);

        index = totalValue > 999 ? 5 : Math.min(index, numStyles); // To assign the m5 icon to 1k+ always
        return {
          text:
            totalValue > 999
              ? '1K+'
              : totalValue > 99
              ? '99+'
              : totalValue.toString(),
          index: index,
          title: totalValue > 1 ? t('REPORTED_OUTAGES') : t('REPORTED_OUTAGE'),
        };
      },
      imagePath: `${getImagePath()}groupedOutages/m`, // so you must have m1.png, m2.png, m3.png, m4.png, m5.png and in that folder
    };
    return (
      //maxZoom is used to decluster all the markers on specific zoom level
      // @ts-ignore
      <MarkerClusterer maxZoom={MAX_CLUSTER_ZOOM} options={clustererOptions}>
        {clusterer =>
          finalMarkers.map((location, index) => (
            <Marker
              icon={
                location?.item &&
                (showSelectedEvent &&
                selectedOutageEvent?.eventId === location.item.eventId
                  ? `${getImagePath()}outageEventCountSelected.png`
                  : `${getImagePath()}outageEventCount.png`)
              }
              label={{
                text: location?.item
                  ? `${
                      location.item.totalCustomersImpacted > 999
                        ? '1K+'
                        : location.item.totalCustomersImpacted > 99
                        ? '99+'
                        : location.item.totalCustomersImpacted
                    }`
                  : '',
                color:
                  location?.item &&
                  showSelectedEvent &&
                  selectedOutageEvent?.eventId === location.item.eventId
                    ? colors.errorRed
                    : colors.white,
              }}
              title="Reported Outage"
              key={location.id}
              position={location.position}
              clusterer={clusterer}
              onClick={e => {
                location?.item &&
                  location.onClick &&
                  location.onClick(index, location.item);
              }}
            />
          ))
        }
      </MarkerClusterer>
    );
  };

  const handleMyLocationClick = () => {
    if (map && currentUserLocation) {
      map.setZoom(MIN_START_ZOOM);
      map.setCenter(currentUserLocation);
    }
  };

  const renderMyLocationIcon = () => {
    return (
      <IconButton
        aria-label="My Location"
        className={clsx(
          classes.legendButton,
          isNativeMobileAppPage ? classes.mobAppLegendButton : '',
        )}
        style={{
          top: outageBannerNotificationHeight + (!isPlannedDownTime ? 125 : 50),
        }}
        onClick={handleMyLocationClick}
      >
        <NearMeRoundedIcon
          className={classes.legendButtonIcon}
          viewBox="0 0 40 40"
        />
      </IconButton>
    );
  };

  const mapOptions: google.maps.MapOptions = {
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeId: mapType,
    minZoom: MIN_MAP_ZOOM,
    maxZoom: MAX_MAP_ZOOM,
    zoomControl: isMobile ? false : true,
    restriction: {
      latLngBounds: PGE_SERVICE_BOUND_RESTRICTION,
      strictBounds: false,
    },
    gestureHandling: 'greedy',
  };

  const { isAuthenticated } = useAuth();

  return (
    <div
      className={`${classes.root} ${classes.rootContainer}`}
      id={id}
      data-testid="map"
    >
      {!map || !currentUserLocation ? null : renderMyLocationIcon()}
      <MapElement
        width={'100%'}
        mapProps={{
          onLoad: loadedMap => setMap(loadedMap),
          onDragStart: handleMapMoved,
          options: { ...mapOptions },
          mapContainerClassName: 'mapContainterRoot',
        }}
        zoom={isAuthenticated ? 12 : 9}
      >
        {!mapData || !map ? null : renderOutages()}
        {!map || !accountsDisplay
          ? null
          : renderAccountMarkers(accountsDisplay)}
        {!map || !currentUserLocation
          ? null
          : showCurrentLocation(currentUserLocation)}
        {children}
        <Data
          options={{
            style: {
              strokeOpacity: 0.5,
              fillOpacity: 0,
              strokeColor: colors.darkBlue2,
            },
          }}
          onLoad={data => {
            data.addGeoJson(geoJsonFeature);
          }}
        />
      </MapElement>
    </div>
  );
};
const OutageCenterMap = React.memo(OutageCenterMapCore);
export default OutageCenterMap;
